import { z } from "zod"
import { type Locale, locales } from "./locale"

export const translatableFieldSchema = z.strictObject({
  primaryLocale: z.enum(locales),
  values: z.record(z.string(), z.string()).transform(values => {
    const filtered: Partial<Record<Locale, string>> = {}
    for (const [key, value] of Object.entries(values)) {
      if (locales.includes(key as Locale)) {
        filtered[key as Locale] = value
      }
    }
    return filtered
  }),
})

export type TranslatableData = z.infer<typeof translatableFieldSchema>

export function parseTranslatableField(value: string): TranslatableData | null {
  const parsed = z
    .string()
    .transform((val, ctx) => {
      try {
        return JSON.parse(val)
      } catch {
        ctx.addIssue({
          code: "custom",
          message: "Invalid JSON",
        })
        return z.NEVER
      }
    })
    .pipe(translatableFieldSchema)
    .safeParse(value)

  return parsed.success ? parsed.data : null
}

export function translate(
  field: string | null | undefined,
  locale: Locale,
): string | null {
  if (field === null || field === undefined) return null

  const parsed = parseTranslatableField(field)
  if (!parsed) return field

  const { values, primaryLocale } = parsed
  const requestedValue = values[locale]
  if (requestedValue !== undefined) return requestedValue

  const primaryValue = values[primaryLocale]
  if (primaryValue !== undefined) return primaryValue

  return Object.values(values)[0] ?? null
}

export function translatableStringSchema(maxLength?: number) {
  const base = maxLength !== undefined ? z.string().max(maxLength) : z.string()
  return base.refine(
    val => {
      if (!val) return true
      try {
        const parsed = JSON.parse(val)
        const result = translatableFieldSchema.safeParse(parsed)
        return result.success
      } catch {
        return true
      }
    },
    { message: "Invalid translatable field format" },
  )
}

export function buildTranslatableField(
  getText: (locale: Locale) => string,
  primaryLocale: Locale,
  availableLocales: Locale[] = locales,
): string {
  const values: Record<string, string> = {}
  for (const locale of availableLocales) {
    values[locale] = getText(locale)
  }
  return JSON.stringify({
    primaryLocale,
    values,
  } satisfies TranslatableData)
}

export function isEmptyTranslatableField(
  value: string | null | undefined,
): boolean {
  if (value === null || value === undefined) return true
  if (value.trim() === "") return true

  const parsed = parseTranslatableField(value)
  if (!parsed) return false

  const values = parsed.values
  return (
    Object.keys(values).length === 0 ||
    Object.values(values).every(v => !v || String(v).trim() === "")
  )
}
